import { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Search, Loader2 } from "lucide-react";

/**
 * GooglePlacePicker — champ combiné :
 *   - Un input avec Places Autocomplete
 *   - Une petite carte qui recentre sur la sélection
 *   - Callback onPick(place) avec { name, address, city, postal_code, country, lat, lng, google_place_id }
 *
 * Props :
 *   value  : { name, address, city, postal_code, country, lat, lng, google_place_id } (contrôlé)
 *   onPick : (place) => void
 *   label  : label affiché au-dessus (optionnel)
 *   showMap: bool (défaut true)
 */
export default function GooglePlacePicker({ value = {}, onPick, label = "Rechercher un lieu", showMap = true, dataTestId = "gplace-picker", existingPlaces = [] }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return <div className="text-xs text-destructive">Clé Google Maps manquante (REACT_APP_GOOGLE_MAPS_API_KEY).</div>;
  }
  return (
    <APIProvider apiKey={apiKey} libraries={["places"]}>
      <div className="space-y-2" data-testid={dataTestId}>
        {label && <Label className="text-xs">{label}</Label>}
        <PlacesAutocompleteField value={value} onPick={onPick} existingPlaces={existingPlaces} />
        {showMap && value?.lat && value?.lng && (
          <div className="rounded-lg overflow-hidden border h-56">
            <Map
              defaultCenter={{ lat: value.lat, lng: value.lng }}
              defaultZoom={15}
              center={{ lat: value.lat, lng: value.lng }}
              gestureHandling="greedy"
              disableDefaultUI={true}
              zoomControl={true}
              mapId="aae-map"
              style={{ width: "100%", height: "100%" }}
            >
              <AdvancedMarker position={{ lat: value.lat, lng: value.lng }}>
                <div className="w-6 h-6 rounded-full bg-primary border-2 border-primary-foreground shadow-glow flex items-center justify-center">
                  <MapPin className="h-3 w-3 text-primary-foreground" />
                </div>
              </AdvancedMarker>
            </Map>
          </div>
        )}
      </div>
    </APIProvider>
  );
}

function PlacesAutocompleteField({ value, onPick, existingPlaces = [] }) {
  const placesLib = useMapsLibrary("places");
  const inputRef = useRef(null);
  const [text, setText] = useState(value?.name || "");
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [service, setService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => { setText(value?.name || ""); }, [value?.name]);

  useEffect(() => {
    if (!placesLib) return;
    setService(new placesLib.AutocompleteService());
    const container = document.createElement("div");
    setPlacesService(new placesLib.PlacesService(container));
    setSessionToken(new placesLib.AutocompleteSessionToken());
  }, [placesLib]);

  useEffect(() => {
    if (!service || !text || text.length < 2) { setPredictions([]); return; }
    setLoading(true);
    service.getPlacePredictions(
      { input: text, sessionToken, componentRestrictions: { country: ["fr", "be", "ch", "lu", "mc"] } },
      (preds) => {
        setPredictions(preds || []);
        setLoading(false);
      }
    );
  }, [text, service, sessionToken]);

  // Suggestions locales (carnet des lieux) : match sur le nom
  const localMatches = (() => {
    const v = (text || "").trim().toLowerCase();
    if (v.length < 2) return [];
    return (existingPlaces || [])
      .filter(p => (p.name || "").toLowerCase().includes(v))
      .slice(0, 5);
  })();

  const selectExisting = (p) => {
    setText(p.name);
    setOpen(false);
    setPredictions([]);
    onPick?.({
      id: p.id,
      name: p.name,
      address: p.address || "",
      city: p.city || "",
      postal_code: p.postal_code || "",
      country: p.country || "France",
      lat: p.lat,
      lng: p.lng,
      google_place_id: p.google_place_id || "",
      spaces: p.spaces || [],
    });
  };

  const selectPrediction = (pred) => {
    if (!placesService || !pred) return;
    placesService.getDetails(
      { placeId: pred.place_id, sessionToken, fields: ["name", "formatted_address", "address_components", "geometry", "place_id"] },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        const comp = (type) => place.address_components?.find(c => c.types.includes(type))?.long_name || "";
        const short = (type) => place.address_components?.find(c => c.types.includes(type))?.short_name || "";
        const picked = {
          name: place.name || pred.structured_formatting?.main_text || pred.description,
          address: place.formatted_address || "",
          city: comp("locality") || comp("postal_town") || comp("administrative_area_level_2"),
          postal_code: comp("postal_code"),
          country: comp("country") || "France",
          country_code: short("country"),
          lat: place.geometry?.location?.lat?.(),
          lng: place.geometry?.location?.lng?.(),
          google_place_id: place.place_id,
        };
        setText(picked.name);
        setOpen(false);
        setPredictions([]);
        onPick?.(picked);
        setSessionToken(placesLib ? new placesLib.AutocompleteSessionToken() : null);
      }
    );
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary pointer-events-none" />
        <Input
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); onPick?.({ ...value, name: e.target.value }); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="Nom, lieu, adresse…"
          className="pl-9"
          data-testid="gplace-search-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary" />}
      </div>
      {open && (localMatches.length > 0 || predictions.length > 0) && (
        <ul className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border bg-popover shadow-md max-h-72 overflow-auto" data-testid="gplace-predictions">
          {localMatches.length > 0 && (
            <li className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-primary font-bold border-b">Carnet des lieux</li>
          )}
          {localMatches.map((p) => (
            <li key={`local-${p.id}`}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectExisting(p); }}
                className="w-full text-left px-3 py-2 hover:bg-muted flex items-start gap-2"
                data-testid={`gplace-local-${p.id}`}
              >
                <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {[p.postal_code, p.city].filter(Boolean).join(" ") || p.address || ""}
                    {p.spaces && p.spaces.length > 0 && <span className="text-primary"> · {p.spaces.length} espace(s)</span>}
                  </div>
                </div>
              </button>
            </li>
          ))}
          {predictions.length > 0 && (
            <li className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-primary font-bold border-b border-t">Google Maps</li>
          )}
          {predictions.map((p) => (
            <li key={p.place_id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectPrediction(p); }}
                className="w-full text-left px-3 py-2 hover:bg-muted flex items-start gap-2"
                data-testid={`gplace-pred-${p.place_id}`}
              >
                <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{p.structured_formatting?.main_text || p.description}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{p.structured_formatting?.secondary_text || ""}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
